import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Autocomplete,
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { adminAPI, eventBatchAPI, trekAPI } from "../services/api";
import {
  computePackageDates,
  formatPackageDateRange,
} from "../utils/packageDates.js";
import { resolveBookingPackage } from "../utils/participantPaymentShare.js";
import { sortEventBatchesByStartDateAsc } from "../utils/sortEventBatchesByStartDateDesc.js";
import { fetchAllEventBatches } from "../utils/fetchAllEventBatches.js";
import useListQueryParams from "../hooks/useListQueryParams";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { formatAdminInr } from "../utils/formatAdminPrice.js";
import { displayReceiptNumber } from "../utils/displayReceiptNumber.js";
import { formatPersonDisplayName } from "../utils/capitalizePersonName.js";
import { openAdminReceiptPrintEnriched } from "../utils/registrationReceiptView.js";
import { formatAdminIstDateTime } from "../utils/adminIstDateTime.js";
import AdminDateRangeFilter from "../components/AdminDateRangeFilter.jsx";
import {
  PageHeader,
  FilterBar,
  EmptyState,
  StatusChip,
} from "../components/ui";
import {
  RECEIPT_PAYMENT_METHOD_OPTIONS,
  receiptPaymentMethodLabel,
} from "../constants/receiptPaymentMethods.js";
import {
  registrationStatusLabel,
  REGISTRATION_STATUS_OPTIONS,
} from "../constants/registrationStatuses.js";
import { getEffectiveRegistrationStatus } from "../utils/participantPaymentShare.js";
import "./Registrations.css";
function formatDate(d) {
  if (!d) return "—";
  const x = new Date(d);
  return isNaN(x.getTime())
    ? "—"
    : x.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}
const formatDateTime = formatAdminIstDateTime;

function resolveReceiptRegistrationStatus(row) {
  const booking = row?.booking;
  if (!booking) return "confirmed";
  const baseStatus = booking.registration?.status;
  const baseNorm = String(baseStatus || "")
    .trim()
    .toLowerCase();
  // DB registration already marked confirmed — don't show pending when list payload lacks fee breakdown.
  if (baseNorm === "confirmed") return "confirmed";
  const traveler =
    Array.isArray(booking.travelers) && booking.travelers[0]
      ? booking.travelers[0]
      : null;
  return getEffectiveRegistrationStatus(booking, traveler, 0, baseStatus);
}

function resolveReceiptTrekName(receiptRow) {
  const snap = receiptRow?.snapshotJson;
  if (snap?.trekName) return snap.trekName;
  const booking = receiptRow?.booking;
  return (
    booking?.batch?.trek?.name ||
    booking?.registration?.event?.trek?.name ||
    "—"
  );
}

function formatEventDateRange(startDate, endDate) {
  if (!startDate && !endDate) return "—";
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  if (!endDate || start === end) return start;
  return `${start} – ${end}`;
}

function resolveReceiptPackageDates(receiptRow) {
  const snap = receiptRow?.snapshotJson;
  if (snap?.packageStartDate || snap?.packageEndDate) {
    return formatPackageDateRange(
      snap.packageStartDate,
      snap.packageEndDate,
      formatDate,
    );
  }
  const booking = receiptRow?.booking;
  const batch = booking?.batch || booking?.registration?.event;
  const trek = batch?.trek;
  const pkg = resolveBookingPackage(booking);
  if (batch && pkg) {
    const { packageStartDate, packageEndDate } = computePackageDates(
      batch,
      pkg,
      trek?.packages,
    );
    if (packageStartDate || packageEndDate) {
      return formatPackageDateRange(
        packageStartDate,
        packageEndDate,
        formatDate,
      );
    }
  }
  if (snap?.batchStartDate || snap?.batchEndDate) {
    return formatEventDateRange(snap.batchStartDate, snap.batchEndDate);
  }
  return formatEventDateRange(batch?.startDate, batch?.endDate);
}

function toDatetimeLocalValue(d) {
  if (!d) return "";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
}

const Receipts = () => {
  const { showToast } = useToast();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canManageReceipts = can("receipts_manage");
  const [editModal, setEditModal] = useState({ open: false, row: null });
  const [editForm, setEditForm] = useState({
    amount: "",
    paidAt: "",
    notes: "",
    method: "office",
    refreshSnapshot: false,
  });
  const { query, setQuery } = useListQueryParams({
    page: "1",
    limit: "50",
    trekId: "",
    batchId: "",
    search: "",
    method: "",
    fromDate: "",
    toDate: "",
    sortOrder: "desc",
    showAll: "",
    statuses: "",
  });
  const page = Number(query.page || 1);
  const limit = Number(query.limit || 50);
  const trekFilter = query.trekId || "";
  const batchFilter = query.batchId || "";
  const search = query.search || "";
  const method = query.method || "";
  const fromDate = query.fromDate || "";
  const toDate = query.toDate || "";
  const sortOrder = query.sortOrder || "desc";
  const showAll = query.showAll === "true";
  const statuses = query.statuses || "";

  const batchListParams = useMemo(
    () => ({
      sortBy: "startDate",
      sortOrder: "asc",
      noCount: "true",
      ...(showAll
        ? { pickerOnly: "true" }
        : { upcomingOnly: "true", limit: 500 }),
    }),
    [showAll],
  );

  const { data: batchesData, isFetching: batchesLoading } = useQuery({
    queryKey: [
      "admin-event-batches-receipts",
      trekFilter,
      showAll,
      batchListParams,
    ],
    queryFn: async () => {
      const base = { ...batchListParams, trekId: trekFilter };
      if (showAll) {
        const rows = await fetchAllEventBatches(base);
        return { data: { data: rows } };
      }
      return eventBatchAPI.list(base);
    },
    enabled: Boolean(trekFilter),
  });
  const allBatches = batchesData?.data?.data || batchesData?.data || [];
  const batches = sortEventBatchesByStartDateAsc(
    trekFilter
      ? allBatches.filter(
          (b) => String(b.trekId || b.trek?.id) === String(trekFilter),
        )
      : [],
  );

  const { data: allTreksData } = useQuery({
    queryKey: ["admin-treks-for-receipts", "all"],
    queryFn: () => trekAPI.list({ page: 1, limit: 500 }),
    enabled: showAll,
  });

  const { data: upcomingBatchesForTreksData } = useQuery({
    queryKey: ["admin-event-batches-for-receipts-trek-filter", showAll],
    queryFn: () => eventBatchAPI.list(batchListParams),
    enabled: !showAll,
  });

  const upcomingTreks = useMemo(() => {
    const rows =
      upcomingBatchesForTreksData?.data?.data ||
      upcomingBatchesForTreksData?.data ||
      [];
    const byId = new Map();
    for (const b of rows) {
      const t = b.trek;
      if (t?.id == null) continue;
      const key = String(t.id);
      if (!byId.has(key)) {
        byId.set(key, { id: t.id, name: t.name, slug: t.slug });
      }
    }
    return [...byId.values()].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || "")),
    );
  }, [upcomingBatchesForTreksData]);

  const treks = showAll ? allTreksData?.data?.data || [] : upcomingTreks;
  const isValidTrekFilter = Boolean(
    trekFilter && treks.some((t) => String(t.id) === String(trekFilter)),
  );
  const isValidBatchFilter = Boolean(
    batchFilter && batches.some((b) => String(b.id) === String(batchFilter)),
  );

  const params = { page, limit };
  if (isValidTrekFilter && isValidBatchFilter) {
    params.trekId = trekFilter;
    params.batchId = batchFilter;
  }
  if (search.trim()) params.search = search.trim();
  if (method) params.method = method;
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;
  if (sortOrder) params.sortOrder = sortOrder;
  if (statuses) params.statuses = statuses;

  const { data, isLoading } = useQuery({
    queryKey: [
      "admin-receipts",
      trekFilter,
      batchFilter,
      search,
      method,
      fromDate,
      toDate,
      sortOrder,
      showAll,
      statuses,
      page,
      limit,
    ],
    queryFn: () => adminAPI.getReceipts(params),
    staleTime: 30_000,
    keepPreviousData: true,
  });

  const updateReceiptMutation = useMutation({
    mutationFn: ({ id, payload }) => adminAPI.updateReceipt(id, payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries(["admin-receipts"]);
      setEditModal({ open: false, row: null });
      showToast({
        message: res?.data?.message || "Receipt updated.",
        severity: "success",
      });
    },
    onError: (err) => {
      showToast({
        message: err?.response?.data?.message || "Failed to update receipt.",
        severity: "error",
      });
    },
  });

  const openEditModal = (row) => {
    setEditForm({
      amount: String(row.amount ?? ""),
      paidAt: toDatetimeLocalValue(row.paidAt || row.createdAt),
      notes: row.notes || "",
      method: row.method || "office",
      refreshSnapshot: false,
    });
    setEditModal({ open: true, row });
  };

  const saveReceiptEdit = () => {
    const row = editModal.row;
    if (!row?.id) return;
    const amt = Number(editForm.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      showToast({ message: "Enter a valid amount.", severity: "warning" });
      return;
    }
    updateReceiptMutation.mutate({
      id: row.id,
      payload: {
        amount: amt,
        paidAt: editForm.paidAt || undefined,
        notes: editForm.notes,
        method: editForm.method,
        refreshSnapshot: editForm.refreshSnapshot,
      },
    });
  };

  const list = data?.data?.data || [];
  const pagination = data?.data?.pagination || {
    page: 1,
    totalPages: 1,
    total: 0,
  };
  const [exportingCsv, setExportingCsv] = useState(false);

  const buildReceiptFilterParams = () => {
    const p = {};
    if (isValidTrekFilter && isValidBatchFilter) {
      p.trekId = trekFilter;
      p.batchId = batchFilter;
    }
    if (search.trim()) p.search = search.trim();
    if (method) p.method = method;
    if (fromDate) p.fromDate = fromDate;
    if (toDate) p.toDate = toDate;
    if (sortOrder) p.sortOrder = sortOrder;
    if (statuses) p.statuses = statuses;
    return p;
  };

  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const res = await adminAPI.exportReceiptsCsv(buildReceiptFilterParams());
      const contentType = String(res.headers["content-type"] || "");
      if (
        contentType.includes("application/json") ||
        contentType.includes("text/html")
      ) {
        const text =
          typeof res.data === "string"
            ? res.data
            : await new Response(res.data).text();
        let message = "Could not download receipts CSV.";
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {
          if (/502|Bad Gateway/i.test(text)) {
            message =
              "API is down (502). Restart the backend, then try Export Excel again.";
          }
        }
        throw Object.assign(new Error(message), { handled: true });
      }
      const disposition = String(res.headers["content-disposition"] || "");
      const q = disposition.match(/filename="([^"]+)"/i);
      const filename =
        q?.[1] ||
        `receipts-export-${new Date().toISOString().slice(0, 10)}.csv`;
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      const rowCount = Number(res.headers["x-export-row-count"]);
      const truncated =
        String(res.headers["x-export-truncated"] || "").toLowerCase() ===
        "true";
      showToast({
        message: truncated
          ? `Downloaded ${Number.isFinite(rowCount) ? rowCount.toLocaleString("en-IN") : "many"} row(s) (capped — narrow filters for full export).`
          : `Downloaded ${Number.isFinite(rowCount) ? rowCount.toLocaleString("en-IN") : ""} receipt(s).`.trim(),
        severity: "success",
      });
    } catch (err) {
      showToast({
        message: err?.handled
          ? err.message
          : err?.response?.status === 502
            ? "API is down (502). Restart the backend, then try Export Excel again."
            : err?.response?.status === 404
              ? "CSV export API not deployed yet. Redeploy backend, then try again."
              : err?.response?.data?.message ||
                err?.message ||
                "Could not download receipts CSV.",
        severity: "error",
      });
    } finally {
      setExportingCsv(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Receipts"
        subtitle={
          <>
            Per-row <strong>Print receipt</strong> opens a print dialog (choose
            Save as PDF). Use <strong>Export Excel (CSV)</strong> for a
            spreadsheet of all receipts matching your filters.
          </>
        }
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadRoundedIcon />}
            disabled={exportingCsv || !pagination.total}
            onClick={handleExportCsv}
          >
            {exportingCsv ? "Downloading…" : "Export Excel (CSV)"}
          </Button>
        }
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 2, maxWidth: 720 }}
      >
        Rows are sorted by <strong>receipt</strong> time. Pick a trek and event
        to browse receipts for that batch, or search by receipt number / notes.
        Printed receipts use a frozen snapshot; use <strong>Edit</strong> to fix
        amount, notes, or refresh event/participants when needed.
      </Typography>

      <FilterBar contentSx={{ py: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 240, width: 260 }}
              options={treks}
              getOptionLabel={(t) => t?.name ?? ""}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              value={
                treks.find((t) => String(t.id) === String(trekFilter)) ?? null
              }
              onChange={(_, trek) =>
                setQuery({ trekId: trek ? String(trek.id) : "", batchId: "" })
              }
              noOptionsText="No treks found"
              ListboxProps={{ style: { maxHeight: 320 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Trek"
                  placeholder="Search trek…"
                />
              )}
            />
            {trekFilter ? (
              <IconButton
                size="small"
                aria-label="Clear trek"
                onClick={() => setQuery({ trekId: "", batchId: "" })}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 240, width: 260 }}
              disabled={!trekFilter}
              loading={batchesLoading}
              options={batches}
              getOptionLabel={(b) =>
                b
                  ? `${formatDate(b.startDate)} to ${formatDate(b.endDate)}`
                  : ""
              }
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              value={
                batches.find((b) => String(b.id) === String(batchFilter)) ??
                null
              }
              onChange={(_, batch) =>
                setQuery({ batchId: batch ? String(batch.id) : "" })
              }
              noOptionsText={
                trekFilter
                  ? batchesLoading
                    ? "Loading events…"
                    : "No events found"
                  : "Select trek first"
              }
              ListboxProps={{ style: { maxHeight: 320 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Event"
                  placeholder={
                    trekFilter
                      ? batchesLoading
                        ? "Loading events…"
                        : "Pick date batch…"
                      : "Select trek first"
                  }
                />
              )}
            />
            {batchFilter ? (
              <IconButton
                size="small"
                aria-label="Clear event"
                onClick={() => setQuery({ batchId: "" })}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showAll}
                onChange={(e) =>
                  setQuery({
                    showAll: e.target.checked ? "true" : "",
                    trekId: "",
                    batchId: "",
                  })
                }
              />
            }
            label="Show all treks/events"
            sx={{ mr: 0 }}
          />
          <TextField
            size="small"
            label="Search"
            placeholder="Receipt number / notes"
            value={search}
            onChange={(e) => setQuery({ search: e.target.value })}
            sx={{ minWidth: 260 }}
          />
          <TextField
            size="small"
            select
            label="Method"
            value={method}
            onChange={(e) => setQuery({ method: e.target.value })}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            {RECEIPT_PAYMENT_METHOD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <FormControl size="small" sx={{ minWidth: 200, flexShrink: 0 }}>
            <InputLabel id="receipt-status-label">Status</InputLabel>
            <Select
              labelId="receipt-status-label"
              multiple
              value={statuses ? statuses.split(",") : []}
              onChange={(e) => setQuery({ statuses: e.target.value.join(",") })}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) =>
                selected.map((s) => registrationStatusLabel(s)).join(", ")
              }
            >
              {REGISTRATION_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox
                    checked={
                      statuses ? statuses.split(",").includes(opt.value) : false
                    }
                  />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <AdminDateRangeFilter
            startDate={fromDate}
            endDate={toDate}
            onStartDateChange={(v) => setQuery({ fromDate: v })}
            onEndDateChange={(v) => setQuery({ toDate: v })}
          />
          <TextField
            size="small"
            select
            label="Sort"
            value={sortOrder}
            onChange={(e) => setQuery({ sortOrder: e.target.value })}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="desc">Latest first</MenuItem>
            <MenuItem value="asc">Oldest first</MenuItem>
          </TextField>
        </Box>
      </FilterBar>

      <Card className="ops-table-card">
        {isLoading ? (
          <div className="ops-loading-box">
            <CircularProgress size={32} sx={{ color: "primary.main" }} />
            <Typography variant="body2" color="text.secondary">
              Loading receipts…
            </Typography>
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            title="No receipts found"
            description="Try adjusting trek, date, or search filters."
          />
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "#475569",
                      bgcolor: "#f8fafc",
                    }}
                  >
                    Receipt no.
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "#475569",
                      bgcolor: "#f8fafc",
                    }}
                  >
                    Customer
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "#475569",
                      bgcolor: "#f8fafc",
                    }}
                  >
                    Trek / Event
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "#475569",
                      bgcolor: "#f8fafc",
                    }}
                  >
                    Amount
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "#475569",
                      bgcolor: "#f8fafc",
                    }}
                  >
                    Payment
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "#475569",
                      bgcolor: "#f8fafc",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "#475569",
                      bgcolor: "#f8fafc",
                    }}
                  >
                    Created At
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "#475569",
                      bgcolor: "#f8fafc",
                    }}
                  >
                    Created By
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      color: "#475569",
                      bgcolor: "#f8fafc",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((row) => {
                  const b = row.booking;
                  const travelers = Array.isArray(b?.travelers)
                    ? b.travelers
                    : [];
                  const primary = travelers[0] || null;
                  const primaryName = primary
                    ? formatPersonDisplayName(
                        primary.firstName,
                        primary.lastName,
                      )
                    : "—";
                  const paymentLabel = receiptPaymentMethodLabel(row.method);
                  const createdBy =
                    row.recordedByUser?.firstName ||
                    row.recordedByUser?.lastName
                      ? `${row.recordedByUser.firstName} ${row.recordedByUser.lastName}`.trim()
                      : row.recordedByUser?.email || "System";
                  const trekName = resolveReceiptTrekName(row);
                  const packageDates = resolveReceiptPackageDates(row);
                  const receiptRegStatus =
                    resolveReceiptRegistrationStatus(row);

                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>{displayReceiptNumber(row)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {primaryName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 1.5,
                          }}
                        >
                          <Typography variant="body2">{trekName}</Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              whiteSpace: "nowrap",
                              textAlign: "right",
                              flexShrink: 0,
                            }}
                          >
                            {packageDates}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {formatAdminInr(row.amount)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {paymentLabel}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          status={receiptRegStatus}
                          label={registrationStatusLabel(receiptRegStatus)}
                        />
                      </TableCell>
                      <TableCell>
                        {formatDateTime(row.paidAt || row.createdAt)}
                      </TableCell>
                      <TableCell>{createdBy}</TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {canManageReceipts ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditRoundedIcon />}
                              onClick={() => openEditModal(row)}
                            >
                              Edit
                            </Button>
                          ) : null}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadRoundedIcon />}
                            onClick={async () => {
                              try {
                                await openAdminReceiptPrintEnriched(row);
                              } catch (err) {
                                if (err?.message === "POPUP_BLOCKED") {
                                  showToast({
                                    message:
                                      "Pop-up blocked. Allow pop-ups for this site, then click Print receipt again.",
                                    severity: "warning",
                                  });
                                } else {
                                  showToast({
                                    message:
                                      "Failed to open receipt for printing.",
                                    severity: "error",
                                  });
                                }
                              }
                            }}
                          >
                            Print receipt
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {pagination.totalPages > 1 && !isLoading && list.length > 0 ? (
          <div className="ops-table-footer">
            <span className="ops-table-footer-info">
              Page {pagination.page} of {pagination.totalPages} (
              {pagination.total} total)
            </span>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={page <= 1}
                onClick={() =>
                  setQuery({ page: String(page - 1) }, { resetPage: false })
                }
              >
                Previous
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={page >= pagination.totalPages}
                onClick={() =>
                  setQuery({ page: String(page + 1) }, { resetPage: false })
                }
              >
                Next
              </Button>
            </Box>
          </div>
        ) : null}
      </Card>

      <Dialog
        open={editModal.open}
        onClose={() => setEditModal({ open: false, row: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit receipt{" "}
          {editModal.row ? displayReceiptNumber(editModal.row) : ""}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label="Amount (₹)"
            type="number"
            size="small"
            value={editForm.amount}
            onChange={(e) =>
              setEditForm((p) => ({ ...p, amount: e.target.value }))
            }
            inputProps={{ min: 0, step: "0.01" }}
          />
          <TextField
            label="Paid at"
            type="datetime-local"
            size="small"
            value={editForm.paidAt}
            onChange={(e) =>
              setEditForm((p) => ({ ...p, paidAt: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Method"
            size="small"
            value={editForm.method}
            onChange={(e) =>
              setEditForm((p) => ({ ...p, method: e.target.value }))
            }
          >
            {RECEIPT_PAYMENT_METHOD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Notes"
            size="small"
            multiline
            minRows={2}
            value={editForm.notes}
            onChange={(e) =>
              setEditForm((p) => ({ ...p, notes: e.target.value }))
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={editForm.refreshSnapshot}
                onChange={(e) =>
                  setEditForm((p) => ({
                    ...p,
                    refreshSnapshot: e.target.checked,
                  }))
                }
              />
            }
            label="Refresh printed copy (trek, package, package dates, participants from current booking)"
          />
          <Typography variant="caption" color="text.secondary">
            Receipts stay locked when you only change the booking trek. Check
            refresh after fixing group participants or event details on the
            printed tax invoice.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModal({ open: false, row: null })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveReceiptEdit}
            disabled={updateReceiptMutation.isLoading}
          >
            {updateReceiptMutation.isLoading ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Receipts;
